import { useState } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { Shield, Key, FileKey, Upload, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface SecurityStepData {
  encryption: string
  passphrase: string
  remotePath: string
  selectedKeyfile: File | null
}

interface WizardStepSecurityProps {
  mode: 'create' | 'edit' | 'import'
  data: SecurityStepData
  onChange: (data: Partial<SecurityStepData>) => void
}

export default function WizardStepSecurity({ mode, data, onChange }: WizardStepSecurityProps) {
  const { t } = useTranslation()
  const [keyfileMode, setKeyfileMode] = useState<'file' | 'paste'>('file')
  const [keyfileText, setKeyfileText] = useState('')

  const handleKeyfileModeChange = (_: unknown, newMode: 'file' | 'paste' | null) => {
    if (newMode === null) return
    setKeyfileMode(newMode)
    setKeyfileText('')
    onChange({ selectedKeyfile: null })
  }

  const handleKeyfileTextChange = (text: string) => {
    setKeyfileText(text)
    if (text.trim()) {
      const file = new File([text], 'borg_keyfile', { type: 'text/plain' })
      onChange({ selectedKeyfile: file })
    } else {
      onChange({ selectedKeyfile: null })
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Encryption Selection - Only for create mode */}
      {mode === 'create' && (
        <>
          <FormControl fullWidth>
            <InputLabel>{t('wizard.security.encryptionMethodLabel')}</InputLabel>
            <Select
              value={data.encryption}
              label={t('wizard.security.encryptionMethodLabel')}
              onChange={(e) => onChange({ encryption: e.target.value })}
            >
              <MenuItem value="repokey">
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t('wizard.security.repokey')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('wizard.security.repokeyDesc')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="keyfile">
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t('wizard.security.keyfile')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('wizard.security.keyfileDesc')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="none">
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t('wizard.security.none')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('wizard.security.noneDesc')}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {data.encryption === 'none' && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t('wizard.security.securityWarningTitle')}
              </Typography>
              <Typography variant="body2">{t('wizard.security.securityWarningBody')}</Typography>
            </Alert>
          )}
        </>
      )}

      {/* Encryption info for edit mode only */}
      {mode === 'edit' && (
        <Alert severity="info" icon={<Shield size={20} />}>
          <Typography variant="body2">{t('wizard.security.encryptionReadonly')}</Typography>
        </Alert>
      )}

      {/* Passphrase Input */}
      {data.encryption !== 'none' && (
        <TextField
          label={
            mode === 'edit'
              ? t('wizard.security.passphraseOptional')
              : t('wizard.security.passphraseRequired')
          }
          type="password"
          value={data.passphrase}
          onChange={(e) => onChange({ passphrase: e.target.value })}
          placeholder={
            mode === 'edit'
              ? t('wizard.security.passphrasePlaceholderEdit')
              : t('wizard.security.passphrasePlaceholderCreate')
          }
          required={mode !== 'edit'}
          fullWidth
          helperText={
            mode === 'edit'
              ? t('wizard.security.passphraseHelperEdit')
              : t('wizard.security.passphraseHelperCreate')
          }
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, display: 'flex', color: 'text.secondary' }}>
                <Key size={18} />
              </Box>
            ),
          }}
        />
      )}

      {/* Keyfile Upload - Only for import mode */}
      {mode === 'import' && (
        <Box>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <FileKey size={18} />
            {t('wizard.security.borgKeyfileTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {t('wizard.security.borgKeyfileDesc')}
          </Typography>

          <ToggleButtonGroup
            value={keyfileMode}
            exclusive
            onChange={handleKeyfileModeChange}
            size="small"
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="file">
              <Upload size={16} style={{ marginRight: 6 }} />
              {t('wizard.security.uploadFile')}
            </ToggleButton>
            <ToggleButton value="paste">
              <FileText size={16} style={{ marginRight: 6 }} />
              {t('wizard.security.pasteContent')}
            </ToggleButton>
          </ToggleButtonGroup>

          {keyfileMode === 'file' ? (
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<FileKey size={18} />}
              sx={{
                justifyContent: 'flex-start',
                py: 1.5,
                borderStyle: 'dashed',
                '&:hover': {
                  borderStyle: 'solid',
                },
              }}
            >
              {data.selectedKeyfile
                ? t('wizard.security.selectedKeyfile', { name: data.selectedKeyfile.name })
                : t('wizard.security.chooseKeyfile')}
              <input
                type="file"
                hidden
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    onChange({ selectedKeyfile: e.target.files[0] })
                  }
                }}
              />
            </Button>
          ) : (
            <TextField
              multiline
              rows={6}
              fullWidth
              placeholder="BORG_KEY ..."
              value={keyfileText}
              onChange={(e) => handleKeyfileTextChange(e.target.value)}
              inputProps={{
                style: { fontFamily: 'monospace', fontSize: '0.85rem' },
              }}
            />
          )}

          {data.selectedKeyfile && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {keyfileMode === 'file'
                ? t('wizard.security.keyfileUploadNote')
                : t('wizard.security.keyfileContentNote')}
            </Alert>
          )}
        </Box>
      )}

      {/* Remote Path */}
      <TextField
        label={t('wizard.security.remoteBorgPath')}
        value={data.remotePath}
        onChange={(e) => onChange({ remotePath: e.target.value })}
        placeholder="/usr/local/bin/borg"
        fullWidth
        helperText={t('wizard.security.remoteBorgPathHelper')}
      />
    </Box>
  )
}
